import { Injectable } from '@angular/core';

/**
 * Servicio para gestionar el stack de modales abiertos
 * Maneja correctamente el overflow del body cuando hay modales anidados
 * 
 * MEJORAS DE NIVEL SENIOR:
 * - Prevención de fugas de memoria con límites de seguridad
 * - Auto-recuperación ante inconsistencias
 * - Detección de modales huérfanos
 * - Logging para debugging
 */
@Injectable({
  providedIn: 'root'
})
export class ModalStackService {
  private openModalsCount = 0;
  private readonly MAX_MODALS = 10; // Límite de seguridad
  private readonly RECOVERY_CHECK_INTERVAL = 5000; // 5 segundos
  private recoveryTimer: any = null;
  private modalIds: Set<string> = new Set(); // Track modal IDs

  constructor() {
    // Auto-recuperación periódica en caso de inconsistencias
    if (typeof window !== 'undefined') {
      this.startRecoveryCheck();
    }
  }

  /**
   * Incrementa el contador de modales abiertos y aplica overflow: hidden al body
   * Incluye validación de límites de seguridad
   */
  onModalOpen(modalId?: string): void {
    // Si se proporciona ID, verificar que no esté ya registrado
    if (modalId) {
      if (this.modalIds.has(modalId)) {
        console.warn(`[ModalStack] Modal ${modalId} ya está abierto, ignorando duplicado`);
        return;
      }
      this.modalIds.add(modalId);
    }

    // Límite de seguridad para prevenir fugas
    if (this.openModalsCount >= this.MAX_MODALS) {
      console.error('[ModalStack] Límite de modales alcanzado, forzando reset');
      this.forceReset();
      return;
    }

    this.openModalsCount++;
    
    if (this.openModalsCount === 1) {
      this.setBodyOverflow('hidden');
    }
  }

  /**
   * Decrementa el contador de modales abiertos
   * Solo restaura el overflow del body cuando no quedan modales abiertos
   */
  /**
   * Si este modal es el de más arriba de la pila.
   *
   * <p>Lo usa {@code ModalComponent} para que **solo el modal superior reaccione a ESC**. Cada
   * instancia escucha `document:keydown.escape`, así que con dos modales abiertos una sola pulsación
   * los cerraba los dos: el usuario cancelaba un diálogo anidado y perdía también la pantalla desde
   * la que lo abrió.</p>
   *
   * <p>El {@code Set} conserva el orden de inserción, así que el último registrado es el de encima.</p>
   */
  isTopModal(modalId: string): boolean {
    if (this.modalIds.size === 0) return true;
    let ultimo = '';
    for (const id of this.modalIds) ultimo = id;
    return ultimo === modalId;
  }

  onModalClose(modalId?: string): void {
    // Si se proporciona ID, removerlo del set
    if (modalId) {
      this.modalIds.delete(modalId);
    }

    if (this.openModalsCount <= 0) {
      console.warn('[ModalStack] Intento de cerrar modal cuando contador es 0');
      this.forceReset(); // Auto-recuperación
      return;
    }

    this.openModalsCount = Math.max(0, this.openModalsCount - 1);
    
    if (this.openModalsCount === 0) {
      this.setBodyOverflow('');
    }
  }

  /**
   * Fuerza el reset del contador (útil para debugging o casos especiales)
   */
  reset(): void {
    this.forceReset();
  }

  /**
   * Reset forzado interno
   */
  private forceReset(): void {
    this.openModalsCount = 0;
    this.modalIds.clear();
    this.setBodyOverflow('');
  }

  /**
   * Establece el overflow del body de forma segura
   */
  private setBodyOverflow(value: string): void {
    if (typeof document !== 'undefined' && document.body) {
      document.body.style.overflow = value;
    }
  }

  /**
   * Obtiene el número actual de modales abiertos
   */
  getOpenModalsCount(): number {
    return this.openModalsCount;
  }

  /**
   * Método para debugging - obtener estado completo del servicio
   * Úsalo en la consola del navegador:
   * window['modalStackDebug']()
   */
  getDebugInfo(): any {
    return {
      openModalsCount: this.openModalsCount,
      modalIds: Array.from(this.modalIds),
      bodyOverflow: document.body.style.overflow,
      modalsInDOM: document.querySelectorAll('[class*="modal-overlay-animated"]').length,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Verificación periódica de inconsistencias
   * Si no hay modales visibles en el DOM pero el contador es > 0, resetea
   */
  private startRecoveryCheck(): void {
    this.recoveryTimer = setInterval(() => {
      if (this.openModalsCount > 0) {
        const modalsInDOM = document.querySelectorAll('[class*="modal-overlay-animated"]').length;
        
        if (modalsInDOM === 0) {
          console.warn(
            `[ModalStack] Inconsistencia detectada: contador=${this.openModalsCount}, ` +
            `modales en DOM=${modalsInDOM}. Ejecutando auto-recuperación...`
          );
          this.forceReset();
        }
      }
    }, this.RECOVERY_CHECK_INTERVAL);
  }

  /**
   * Limpia el timer de recuperación
   */
  ngOnDestroy(): void {
    if (this.recoveryTimer) {
      clearInterval(this.recoveryTimer);
    }
  }
}
